import { useEffect, useState } from "react";
import type { CSSProperties, FormEvent } from "react";
import { useIstariStore } from "../state/store";

interface HealthResponse {
  status: string;
  version: string;
}

const rowStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  background: "#21213a",
  borderRadius: 8,
  padding: "0.7rem 0.9rem",
  marginBottom: "0.5rem",
};

/** Home page — project list + create, plus server health. */
export function Home() {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const projects = useIstariStore((s) => s.projects);
  const projectsStatus = useIstariStore((s) => s.projectsStatus);
  const projectsError = useIstariStore((s) => s.projectsError);
  const loadProjects = useIstariStore((s) => s.loadProjects);
  const createProject = useIstariStore((s) => s.createProject);

  useEffect(() => {
    fetch("/healthz")
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<HealthResponse>;
      })
      .then(setHealth)
      .catch((e: unknown) => setHealthError(e instanceof Error ? e.message : String(e)));
    void loadProjects();
  }, [loadProjects]);

  const onCreate = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      await createProject(trimmed);
      setName("");
    } catch (e: unknown) {
      setCreateError(e instanceof Error ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <main
      style={{
        fontFamily: "sans-serif",
        padding: "2rem",
        maxWidth: 720,
        margin: "0 auto",
        background: "#1a1a2e",
        color: "#d8d8e8",
        minHeight: "100vh",
        boxSizing: "border-box",
      }}
    >
      <h1 style={{ marginTop: 0 }}>istari</h1>
      {healthError && (
        <p data-testid="health-error" style={{ color: "#e06c75" }}>
          Server unreachable: {healthError}
        </p>
      )}
      {health && (
        <p data-testid="health-status" style={{ color: "#8e8eb8" }}>
          Server: <strong>{health.status}</strong> (v{health.version})
        </p>
      )}
      {!health && !healthError && <p>Connecting to server…</p>}

      <h2 style={{ fontSize: "1.1rem" }}>Projects</h2>
      <form
        onSubmit={(e) => void onCreate(e)}
        style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}
      >
        <input
          data-testid="project-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New project name"
          style={{
            flex: 1,
            background: "#21213a",
            color: "#d8d8e8",
            border: "1px solid #44446a",
            borderRadius: 6,
            padding: "0.5rem 0.6rem",
          }}
        />
        <button
          type="submit"
          data-testid="project-create-button"
          disabled={creating || name.trim() === ""}
          style={{
            padding: "0.5rem 1rem",
            borderRadius: 6,
            border: "1px solid #4a90d9",
            background: "#4a90d9",
            color: "#fff",
            cursor: creating ? "wait" : "pointer",
          }}
        >
          Create project
        </button>
      </form>
      {createError && (
        <p data-testid="project-create-error" style={{ color: "#e06c75" }}>
          Could not create project: {createError}
        </p>
      )}

      {projectsStatus === "loading" && <p>Loading projects…</p>}
      {projectsStatus === "error" && (
        <p data-testid="projects-error" style={{ color: "#e06c75" }}>
          Could not load projects: {projectsError}
        </p>
      )}
      {projectsStatus === "ready" && projects.length === 0 && (
        <p data-testid="projects-empty" style={{ color: "#8e8eb8" }}>
          No projects yet — create one above.
        </p>
      )}
      <ul data-testid="project-list" style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {projects.map((project) => (
          <li key={project.id} data-testid="project-row" style={rowStyle}>
            <a href={`/projects/${project.id}`} style={{ color: "#4a90d9", fontWeight: 600 }}>
              {project.name}
            </a>
            {project.description && (
              <span style={{ color: "#8e8eb8", fontSize: "0.8rem" }}>{project.description}</span>
            )}
          </li>
        ))}
      </ul>

      <nav style={{ marginTop: "2rem", display: "flex", gap: "1.5rem" }}>
        <a href="/viewer" style={{ color: "#4a90d9" }}>
          Open 3D Viewer
        </a>
        <a
          data-testid="codes-monitor-link"
          href="/codes-monitor"
          title="Jurisdiction change monitor — human review queue (istari never auto-updates rules)"
          style={{ color: "#4a90d9" }}
        >
          Codes monitor
        </a>
      </nav>
    </main>
  );
}
